/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */

import { Injectable, inject } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { debounceTime, map, switchMap, take, tap } from 'rxjs/operators';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { IDockedTicketExternalData, TicketEditCommonService } from './ticket-edit-common.service';
import { TicketEditService } from 'src/app/modules/ticket-edit/ticket-edit.service';
import { Side, Ticket } from '@touchplan/common';
import { PlanState } from '../../../services/plan-state.service';
import { DynamicFlagTypes } from '@touchplan/feature-flags';

// public api of the common ticket edit
type IPublicTicketEdit = Pick<TicketEditCommonService, keyof TicketEditCommonService>;

@Injectable({ providedIn: 'root' })
// a desktop only variant of this service, meant to have the same public interface as the "normal" one
// but to selectively use the legacy ticket edit service when the flag is off
export class TicketEditCommonDesktopService implements IPublicTicketEdit {
	private featureFlagManager = inject(FeatureFlagManager);
	private ticketEditCommonService = inject(TicketEditCommonService);
	private legacyTicketEditService = inject(TicketEditService);

	// Usage of planState allowed since this is a short-lived service and this class and these usages will be
	// removed when docked ticket fully replaces ticket edit including to multi-edit scenarios
	private planState = inject(PlanState);

	public dockedTicketEnabled$: Observable<boolean> = this.ticketEditCommonService.dockedTicketEnabled$;

	private get dockedTicketEnabledSync() {
		return this.featureFlagManager.getFlag(DynamicFlagTypes.DOCKED_TICKET_IN_DESKTOP_ENABLED);
	}

	public uiInitialized$ = this.dockedTicketEnabled$.pipe(
		switchMap((enabled) => {
			if (enabled) {
				return this.ticketEditCommonService.uiInitialized$.pipe(
					tap((init) => console.log('TicketEditCommonDesktopService initialized:', init))
				);
			} else {
				return this.legacyIsEditOpenForCycle();
			}
		})
	);

	public registerInstance(instance: IDockedTicketExternalData) {
		return this.ticketEditCommonService.registerInstance(instance);
	}
	public unregisterInstance(id: string) {
		this.ticketEditCommonService.unregisterInstance(id);
	}

	public isEditValid(): Observable<boolean> {
		return this.dockedTicketEnabled$.pipe(
			switchMap((enabled) => {
				if (enabled) {
					return this.ticketEditCommonService.isEditValid();
				} else {
					return this.legacyTicketEditService.isEditValid$;
				}
			})
		);
	}

	public isEditValidSync(): boolean {
		if (this.dockedTicketEnabledSync) {
			return this.ticketEditCommonService.isEditValidSync();
		} else {
			return this.legacyTicketEditService.isEditValidSync;
		}
	}

	/**
	 * Returns an observable indicating true when form is dirty or false when it is not dirty, ie no edits made.
	 * (Does not support old ticket edit)
	 */
	public isTicketFormDirty() {
		return this.ticketEditCommonService.isTicketFormDirty();
	}

	/**
	 * Returns true when form is dirty or false when it is not dirty, ie no edits made yet.
	 */
	public isTicketFormDirtySync() {
		if (this.dockedTicketEnabledSync) {
			return this.ticketEditCommonService.isTicketFormDirtySync();
		} else {
			return this.legacyTicketEditService.isFormDirty();
		}
	}

	/**
	 * Persists the current edited tickets.
	 * If docked ticket feature flag on, it will call the common service to try the save, and if the form is
	 * dirty, it will display the save message and persist the data.
	 * If the docked ticket feature flag is off, it will call the legacy service to display the save message
	 * if the form is dirty, and it will then persist the data.
	 */
	public trySave() {
		this.dockedTicketEnabled$.pipe(take(1)).subscribe((enabled) => {
			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7190
			// if we have multiple tickets selected or multi edit open save with the old service
			if (
				!enabled ||
				this.planState.tickets.editTicket$.getValue().size > 1 ||
				this.planState.floatingTickets.editTicket$.getValue().size > 1 ||
				this.planState.tickets.selectedTicket$.getValue().list.size > 1 ||
				this.planState.floatingTickets.selectedTicket$.getValue().list.size > 1
			) {
				// END PLACEHOLDER REPLACE AS PART OF TP-7190
				this.legacyTicketEditService.logImplicitSave();
				const editTicketList = this.planState.floatingTickets.editTicket$.getValue().size
					? this.planState.floatingTickets
					: this.planState.tickets;
				const currentEditTicket = editTicketList.editTicket$.getValue();
				this.planState.actions.persistTicketData(currentEditTicket, editTicketList);
			} else {
				this.ticketEditCommonService.trySave();
			}
		});
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7217
	// Might be able to resolve this better when we fix the rest of docked ticket.
	// If we create some sort of ticket switching service, this should be replaced with that.
	/**
	 * If the ticket edit is open and is valid, save and close the form.
	 * Returns false if edit was invalid and can't be saved.
	 * This is used only by floating ticket to save before creating a new ticket.
	 */
	public async trySaveAndClose() {
		if (this.dockedTicketEnabledSync) {
			this.ticketEditCommonService.trySaveAndClose();
		} else {
			this.legacyTicketEditService.logImplicitSave();
			this.legacyTicketEditService.saveEditAndClose();
		}
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-7217

	public applyWorkdaysToForm(workdays: boolean[]) {
		// decide if it's sane to keep a sync representation of the flag around for... simplicity
		this.dockedTicketEnabled$.pipe(take(1)).subscribe((enabled) => {
			if (enabled) {
				this.ticketEditCommonService.applyWorkdaysToForm(workdays);
			} else {
				const currentEtc = this.legacyTicketEditService.combinedEdit$.getValue();
				this.legacyTicketEditService.setWorkdaysFromForm(currentEtc, workdays);
				// mark as dirty so we know we have changes to save
				this.legacyTicketEditService.markFormAsDirty();
			}
		});
	}

	// current inclination, curate everything in this service, never expose the full data
	public getWorkdaysUiMetadata() {
		return this.dockedTicketEnabled$.pipe(
			switchMap((enabled) => {
				if (enabled) {
					return this.ticketEditCommonService.getWorkdaysUiMetadata();
				} else {
					return this.transformLegacyWorkdaysData();
				}
			})
		);
	}

	// cycle tickets is using this to detect if edit is open (I think?) for mostly unknown reasons
	// there are probably better ways...
	private legacyIsEditOpenForCycle() {
		return this.legacyTicketEditService.ticketTemplate$.pipe(
			debounceTime(100),
			map((data) => data === null)
		);
	}

	private transformLegacyWorkdaysData() {
		return this.legacyTicketEditService.combinedEdit$.pipe(
			map((etc) => {
				if (!etc) {
					return null;
				}

				//use the first (this only works because we already don't show on muli)
				const ticketId: string = etc.selectedList.keys().next().value;

				return {
					liveWorkingDays: etc.data.liveWorkingDays,
					ticketId,
					editCount: etc.selectedList.size,
					isActive: etc.data.side !== Side.PULL,
				};
			})
		);
	}

	/**
	 * Returns an observable returning a boolean indicating whether the edit ticket window is open or not.
	 */
	public isTicketEditOpen(): Observable<boolean> {
		return combineLatest([
			this.ticketEditCommonService.isTicketEditOpen(),
			this.legacyTicketEditService.combinedEdit$.pipe(
				map((editTicketCombined) => {
					return !!(editTicketCombined && editTicketCombined.selectedList.size);
				})
			),
		]).pipe(
			map(([dockedTicketOpen, legacyTicketOpen]) => {
				return dockedTicketOpen || legacyTicketOpen;
			})
		);
	}

	/**
	 * Returns true if there is currently an open ticket edit.
	 */
	public isTicketEditOpenSync() {
		if (this.dockedTicketEnabledSync) {
			return this.ticketEditCommonService.isTicketEditOpenSync();
		} else {
			return !!this.legacyTicketEditService.combinedEdit$.getValue()?.selectedList?.size;
		}
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7386
	// This is temporary. It is used to get the Ticket Context in plan component for ticket attachments and
	// docked ticket.  Once ticket attachments is no longer needed, we can see how we can better create the
	// ticket context.
	public getSingleEditTicket$(): Observable<Ticket> {
		return this.legacyTicketEditService.getSingleEditTicket$();
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-7386

	/**
	 * Returns an observable which returns true when a ticket edit was closed with edits (ie it was dirty so
	 * was saved before closing.  This is used by Undo Changes to indicate a change needs to be created)
	 */
	public ticketEditClosedWithChanges() {
		return this.dockedTicketEnabled$.pipe(
			switchMap((enabled) => {
				if (enabled) {
					return this.ticketEditCommonService.ticketEditClosedWithChanges();
				} else {
					return this.legacyTicketEditService.closeEditAndCheckDirty$;
				}
			})
		);
	}
}
